import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Search, Swords } from 'lucide-react';
import Logo from '@/components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function SendInvite() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState('');
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    loadLists();
  }, []);

  useEffect(() => {
    if (searchQuery.length > 2) {
      searchUsers();
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const loadLists = async () => {
    try {
      const response = await axios.get(`${API}/ranking-lists`);
      setLists(response.data);
    } catch (error) {
      toast.error('Failed to load ranking lists');
    }
  };

  const searchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/search?q=${searchQuery}`, {
        withCredentials: true
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!selectedUser || !selectedList) {
      toast.error('Please select a user and ranking list');
      return;
    }

    setIsSending(true);
    try {
      await axios.post(
        `${API}/invites`,
        {
          receiver_email: selectedUser.email,
          list_id: selectedList
        },
        { withCredentials: true }
      );
      toast.success('Match invite sent!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <Button 
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <Logo size="lg" />
        </div>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <Swords className="w-10 h-10 text-[#FF3B30]" />
            <h1 className="text-3xl font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>
              Challenge a Player
            </h1>
          </div>
          <p className="text-gray-400">Send a match invite to another player</p>
        </div>

        <Card className="bg-[#111111] border-[#27272A]">
          <CardHeader>
            <CardTitle className="text-white">Send Match Invite</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSendInvite} className="space-y-6">
              <div>
                <Label htmlFor="search" className="text-white mb-2 block">
                  Search Player by Name or Email
                </Label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
                  <Input
                    id="search"
                    data-testid="search-player-input"
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="bg-[#0A0A0A] border-[#27272A] text-white pl-10"
                  />
                </div>
                
                {searchResults.length > 0 && (
                  <div className="mt-2 bg-[#0A0A0A] border border-[#27272A] rounded-lg overflow-hidden">
                    {searchResults.map(user => (
                      <button
                        key={user.user_id}
                        type="button"
                        data-testid="user-search-result"
                        onClick={() => {
                          setSelectedUser(user);
                          setSearchQuery(user.email);
                          setSearchResults([]);
                        }}
                        className="w-full text-left p-3 hover:bg-[#111111] transition-colors border-b border-[#27272A] last:border-b-0"
                      >
                        <p className="text-white font-bold">{user.name}</p>
                        <p className="text-sm text-gray-400">{user.email}</p>
                        {user.nickname && (
                          <p className="text-xs text-gray-500">@{user.nickname}</p>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {selectedUser && (
                  <div className="mt-3 bg-[#007AFF]/10 border border-[#007AFF]/30 rounded-lg p-3">
                    <p className="text-sm text-gray-400 mb-1">Selected Player:</p>
                    <p className="text-white font-bold">{selectedUser.name}</p>
                    <p className="text-sm text-[#007AFF]">{selectedUser.email}</p>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="list" className="text-white mb-2 block">
                  Select Ranking List
                </Label>
                <Select value={selectedList} onValueChange={setSelectedList}>
                  <SelectTrigger 
                    data-testid="select-ranking-list"
                    className="bg-[#0A0A0A] border-[#27272A] text-white"
                  >
                    <SelectValue placeholder="Choose a ranking list" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-[#27272A]">
                    {lists.map(list => (
                      <SelectItem 
                        key={list.list_id} 
                        value={list.list_id}
                        className="text-white hover:bg-[#111111]"
                      >
                        {list.name} - {list.match_format}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button 
                type="submit"
                data-testid="send-invite-submit-btn"
                disabled={isSending || !selectedUser || !selectedList}
                className="w-full bg-[#FF3B30] hover:bg-[#D6301E] text-white rounded-full font-bold py-6"
              >
                {isSending ? 'Sending...' : (
                  <>
                    <Swords className="w-5 h-5 mr-2" />
                    Send Challenge
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default SendInvite;